import {IcnCopy, IcnQrCode} from "../../assets/icons";

import {useCallback, useEffect, useState} from "react";
import * as Yup from "yup";
import {Form, Formik} from "formik";
import {QRCodeSVG} from "qrcode.react";
import {Slide, toast} from "react-toastify";

import {HomeRepayCreditFormStableCoinValues} from "../../utils";
import {AvailableBalance} from "../../utils/creditService";

import FormFieldset from "../form-elements/FormFieldset";
import BlockToken from "../common/BlockToken";
import FormInput from "../form-elements/FormInput";
import Button from "../common/Button";
import FormGroupHelpText from "../form-elements/FormGroupHelpText";
import {repayCredit} from "../../utils/creditService";

const HomeRepayCreditFormStablecoinSchema = Yup.object().shape({
  tokens: Yup.array().of(Yup.string()).notRequired(),
  walletAddress: Yup.string().required("Please enter wallet address to proceed"),
})

interface HomeRepayCreditFormStablecoinProps {
  creditLine: AvailableBalance;
}
const networks2icon: { [key: string]: string } = {
  "ETHEREUM_MAINNET": "ETH",
  "POLYGON_MAINNET": "MATIC",
  "POLYGON_AMOY": "MATIC",
  "ETHEREUM_TESTNET": "ETH",
  "OPTIMISM_SEPOLIA": "OP",
  "OPTIMISM_MAINNET": "OP",
  "BASE_MAINNET": "BASE",
  "BASE_SEPOLIA": "BASE",
  "SOLANA_MAINNET": "SOL",
  "SOLANA_DEVNET": "SOL",
}

function HomeRepayCreditFormStablecoin({ creditLine }: HomeRepayCreditFormStablecoinProps) {
  const [formData, setFormData] = useState<HomeRepayCreditFormStableCoinValues>(
    // use token and chain from credit
    {
      tokens: [
        "USDC", networks2icon[creditLine.network] || "NA"
      ],
      walletAddress: "",
    }
  )
  const [submitting, setSubmitting] = useState<boolean>(true)
  const [qrCodeValue, setQrCodeValue] = useState<string>("");

  // Fetch the repayment address when selected credit line changes
  useEffect(() => {
    const fetchRepaymentAddress = async () => {
      if (!creditLine?.id) return;

      try {
        const response = await repayCredit(creditLine.id);
        setFormData((prevData) => ({
          ...prevData,
          walletAddress: response.repayment_address,
        }));
        setQrCodeValue(response.repayment_address);
      } catch (error) {
        toast.error("Unable to fetch repayment address. Please try again.");
      } finally {
        setSubmitting(false);
      }
    };
    
    fetchRepaymentAddress();
  }, [creditLine.id]);



  const handleCopyToClipboard = useCallback((value: string) => {
    navigator.clipboard.writeText(qrCodeValue)
      .then(() => {
        toast.success('Copied to clipboard', {
          toastId: value,
          position: "bottom-right",
          autoClose: 1000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          transition: Slide,
        })
      })
      .catch(err => {
        console.error('Failed to copy text: ', err)
        toast.error('Failed to copy to clipboard. Please try again.', {
          toastId: value,
          position: "bottom-right",
          autoClose: 1000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: false,
          progress: undefined,
          transition: Slide,
        })
      })
  }, [qrCodeValue])

  return (
    <Formik
      initialValues={formData}
      validationSchema={HomeRepayCreditFormStablecoinSchema}
      onSubmit={(values, { setSubmitting, setStatus }) => {
        setTimeout(() => {
          setSubmitting(false);
          setStatus('submitted'); // Set form status as 'submitted'
        }, 400);
      }}
    >
      {({ isSubmitting, values, status, errors, isValid, touched }) => (
        <Form
          className="c-form"
          noValidate
        >
          <FormFieldset
            extraClassName="token-currencies"
            legendText='Verify credit currencies'
          >
            <div className="c-form-fieldset-block-token-list">
              {values.tokens.map((token, tokenIdx) => (
                <BlockToken
                  key={tokenIdx}
                  token={token}
                  hasName
                />
              ))}
            </div>

          </FormFieldset>

          <FormFieldset
            extraClassName="qr-code"
            legendText='Pay via QR code'
          >
            <div className="c-form-fieldset-qr-code">

              <div className="c-form-fieldset-qr-code-wrapper">

                {submitting ? (
                  <div className="c-loader"/>
                ) : qrCodeValue ? (
                  <QRCodeSVG
                    value={qrCodeValue}
                    title="Repayment Address"
                    size={205}
                    bgColor="#ffffff"
                    fgColor="#000000"
                    level="L"
                  />
                ) : (
                  !submitting && (
                    <div className="no-qr-message">
                      <i><IcnQrCode/></i>
                      <small>
                        QR code is not available. Please try again later.
                      </small>
                    </div>
                  )
                )}

              </div>

            </div>

          </FormFieldset>

          <div className="c-form-fieldset-separator">
            <span>or</span>
          </div>

          <FormFieldset
            extraClassName="cta"
            legendText='Pay via wallet address'
          >
            <FormInput
              extraClassName="boxed"
              formGroupStyle="block-line"
              labelText="Wallet Address"
              type="text"
              name="walletAddress"
              value={qrCodeValue}
              disabled
              readOnly
              formGroupExtraContent={
                <Button
                  extraClassName="clipboard-copy-btn"
                  title="Copy to clipboard"
                  btnVariant="icon"
                  btnColor="ghost"
                  icon={<IcnCopy/>}
                  onClick={() => handleCopyToClipboard(values.walletAddress)}
                />
              }
            />

            <FormGroupHelpText helpText="Please only send USDC to the wallet address above."/>

          </FormFieldset>
        </Form>
      )}

    </Formik>
  );
}

export default HomeRepayCreditFormStablecoin;